import { Tooltip } from '@usealloy/component-library';
import styled, { css } from 'styled-components';
import { ContainerType } from '../../../types';

const highlightStyle = css`
  background-color: var(--cl-yellow-200);
  width: fit-content;
  border-radius: var(--cl-radius-1);
`;

export const StyledRuleContent = styled.div<{
  isHovered: boolean;
  isHighlighted: boolean;
  isPreview?: boolean;
}>`
  display: flex;
  flex-direction: row;
  position: relative;
  align-items: center;
  flex-wrap: wrap;
  max-width: max-content;

  ${({ isHovered }) => css`
    background-color: ${isHovered ? 'var(--cl-gray-200)' : 'transparent'};
  `}

  ${({ isHighlighted }) => (isHighlighted) && highlightStyle}

  &:focus {
    ${highlightStyle}
  }

  > div {
    display: flex;
    flex-shrink: 0;
    flex-wrap: wrap;
    align-items: center;
    max-width: 100%;
    column-gap: var(--cl-space-1);
  }
`;

export const StyledRuleLine = styled.div<{ isHovered: boolean }>`
  display: flex;
  margin: 0 1px;
  width: 100%;
  min-height: var(--cl-space-5);
  justify-content: space-between;
  flex-wrap: wrap;

  ${({ isHovered }) => (isHovered) && css`
    background-color: var(--cl-gray-200);
  `}
`;

export const StyledRuleLineItemContent = styled.div<{
  hasEndContent: boolean;
  containerType: ContainerType;
}>`
  display: flex;
  flex-direction: row;
  align-items: ${({ containerType }) => (containerType === ContainerType.ISLAND || containerType === ContainerType.TOOLTIP ? 'flex-start' : 'center')};
  flex-grow: 1;
  min-width: 0;
  max-width: ${({ hasEndContent }) => (hasEndContent ? '85%' : '99%')};
`;

export const StyledIndent = styled.div<{ indentLevel: number }>`
  display: inline;
  width: ${({ indentLevel }) => (`${indentLevel * 20}px`)};
  min-height: 0px;
`;

export const StyledRuleClause = styled.span<{
  isEmptyPreviewOutcome: boolean;
  containerType: ContainerType;
}>`
  color: var(--cl-text-default);
  font-weight: 500;
  margin-right: var(--cl-space-2);
  font-size: var(--cl-font-size-2);
  line-height: var(--cl-line-height-3);
  min-height: var(--cl-line-height-3);
  font-family: var(--cl-font-family-mono);
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  flex-shrink: 0;
  ${({ isEmptyPreviewOutcome }) => isEmptyPreviewOutcome && css`
    margin: 3px 0 4px 34px;
  `}
  ${({ containerType }) => (containerType === ContainerType.ISLAND || containerType === ContainerType.TOOLTIP) && css`
    margin-top: 2px;
  `}
`;

export const StyledRuleNumber = styled.div<{
  isLineHovered: boolean;
  isDraggable?: boolean;
}>`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--cl-radius-1);
    font-size: var(--cl-font-size-1);
    text-align: center;
    transition: all 300ms ease;
    margin: 0 var(--cl-space-2) 0 1px;
    height: var(--cl-space-5);
    width: var(--cl-space-5);
    flex-shrink: 0;
  
    ${({ isLineHovered }) => css`
      background-color: ${isLineHovered ? 'var(--cl-gray-500)' : 'var(--cl-gray-100)'};
      color: ${isLineHovered ? 'var(--cl-gray-800)' : 'var(--cl-gray-500)'};
    `}
  
    &:hover {
      color: var(--cl-gray-1100);
      background-color: var(--cl-gray-500);
      ${({ isDraggable }) => css`
        cursor: ${isDraggable ? 'grab' : 'pointer'};
      `}
    }
  `;

export const StyledLineBreakDivider = styled.div<{
  isTriggered: boolean;
}>`
  position: relative;
  width: inherit;
  height: 1px;
  transition: all 300ms ease;

  ${({ isTriggered }) => (isTriggered
    && css`
      position: relative;
      height: 19px;
      visibility: visible;
      border-radius: var(--cl-radius-1);
      background: var(--cl-yellow-100);
  `)}
`;

export const StyledMultiSelectContainer = styled.div<{
  isDisabled?: boolean;
  isInvalid?: boolean;
}>`
  ${({ isDisabled }) => (isDisabled && css`
    cursor: not-allowed;
    color: var(--cl-gray-500);
  `)};

  ${({ isInvalid }) => (isInvalid && css`
    background-color: var(--cl-red-100);
    border-radius: var(--cl-radius-1);
  `)};
`;

export const StyledRuleContentContainer = styled.div<{
  index: number;
  editable: boolean;
}>`
  display: flex;
  pointer-events: ${({ editable }) => (!editable ? 'none' : 'auto')};
  align-items: flex-start;
`;

export const StyledDropdownOptionTooltip = styled(Tooltip)`
  font-size: var(--cl-font-size-1);
  line-height: var(--cl-line-height-1);
  text-align: center;
  max-width: 210px;
`;

export const StyledHighlighter = styled.span<{ isHighlighted: boolean }>`
  ${({ isHighlighted }) => (isHighlighted) && css`
    background-color: var(--cl-yellow-100);
    width: fit-content;
    border-radius: var(--cl-radius-1);
  `}
  display: flex;
  flex-direction: row;
  min-width: 0;
  height: 100%
`;
